import React, { useCallback, useEffect, useRef, useState } from "react";
import Layout from "../../components/layout";
import { Link, useNavigate, useParams } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchPaymentsListData,
  getEditPayment,
  getIsCreateModalOpen,
  getParty,
  getPayments,
  getPaymentsFilter,
  getPaymentsLoading,
  getTotalAmount,
  getTotalInvoice,
  getTotalNoteAmount,
  getTotalPayments,
  getTotalPaymnet,
  setPaymentsFilter,
  toggleCreateModal,
} from "../../features/payments/paymentsSlice";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { toast } from "react-toastify";
import { DELETE, GET, POST, PUT } from "../../services/axios.service";
import PaymentList from "../../components/payments/PaymentList";
import EditForm from "../../components/payments/EditForm";
import PaymentForm from "../../components/payments/PaymentForm";
import NoteForm from "../../components/payments/NoteForm";
import { useCanAccess } from "../../hooks/useCanAccess";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { t } from "../../locales";
import ReactSelect from "react-select";
import { useForm, Controller } from "react-hook-form";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Receipt,
  CreditCard,
  Filter,
  Search,
  ListFilter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { PaymentsFilterType } from "../../types/payments";

// Filter-select styling aligned with the form inputs.
const listSelectStyles: any = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof filterSelectStyles.control === "function" ? (filterSelectStyles.control as any)(base, state) : base),
    borderRadius: "0.5rem",
    fontSize: "1rem",
  }),
  singleValue: (base: any) => ({
    ...(typeof filterSelectStyles.singleValue === "function" ? (filterSelectStyles.singleValue as any)(base, {}) : base),
    fontSize: "1rem",
  }),
  placeholder: (base: any) => ({
    ...(typeof filterSelectStyles.placeholder === "function" ? (filterSelectStyles.placeholder as any)(base, {}) : base),
    fontSize: "1rem",
  }),
  input: (base: any) => ({
    ...base,
    fontSize: "1rem",
  }),
};

const sortByOptions = [
  { value: "id", label: t.payments.sortOptions.id },
  { value: "amount", label: t.payments.sortOptions.amount },
  { value: "created_at", label: t.payments.sortOptions.created_at },
];

const sortOrderOptions = [
  { value: "asc", label: t.payments.sortOrderOptions.asc },
  { value: "desc", label: t.payments.sortOrderOptions.desc },
];

const PaymentsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const loggingUser = useAppSelector(getLoggingUser);
  const selectedBranch = useAppSelector(getSelectedBranch);

  const isCreateModalOpen = useAppSelector(getIsCreateModalOpen);
  const payments = useAppSelector(getPayments);
  const totalPayments = useAppSelector(getTotalPayments);
  const party = useAppSelector(getParty);
  const totalInvoice = useAppSelector(getTotalInvoice);
  const totalPaymnet = useAppSelector(getTotalPaymnet);
  const totalNoteAmount = useAppSelector(getTotalNoteAmount);
  const totalAmount = useAppSelector(getTotalAmount);
  const editPayment = useAppSelector(getEditPayment);
  const loading = useAppSelector(getPaymentsLoading);
  const filter = useAppSelector(getPaymentsFilter);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [showFilter, setShowFilter] = useState(false);

  const { register, control, handleSubmit, reset, getValues } = useForm<PaymentsFilterType>({
    defaultValues: { ...filter },
  });

  const prevBranchRef = useRef(selectedBranch);

  useEffect(() => {
    if (loggingUser?.permissions && selectedBranch && !canAccess("parties.payment")) {
      navigate(route.dashboard);
    }
  }, [loggingUser, selectedBranch]);

  // Re-fetch when branch changes
  useEffect(() => {
    if (prevBranchRef.current !== selectedBranch && selectedBranch) {
      prevBranchRef.current = selectedBranch;
      if (page === 1) {
        fetchPaymentList(1, pageSize);
      } else {
        setPage(1);
      }
    }
  }, [selectedBranch]);

  const handleShowCreateForm = (type: number) => {
    dispatch(toggleCreateModal(type));
  };

  const handleSavePayment = async (data: any) => {
    try {
      if (data && data.id) {
        await PUT(`/v1/payments/${data.id}`, { ...data })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              if (page === 1) {
                fetchPaymentList(page, pageSize);
              } else {
                setPage(1);
              }
              toast.success(response.data.message, { ...(toastStyles as any) });
            } else {
              toast.error(response.data.message, { ...(toastStyles as any) });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, { ...(toastStyles as any) });
          });
      } else {
        await POST("/v1/payments", { ...data })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              if (page === 1) {
                fetchPaymentList(page, pageSize);
              } else {
                setPage(1);
              }
              toast.success(response.data.message, { ...(toastStyles as any) });
            } else {
              toast.error(response.data.message, { ...(toastStyles as any) });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, { ...(toastStyles as any) });
          });
      }
    } catch (error) {
      toast.error(t.common.somethingWentWrong, { ...(toastStyles as any) });
    }
  };

  const handleDeletePayment = async (paymentId: number, password: string) => {
    try {
      await DELETE(`/v1/payments/${paymentId}`, { password })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = payments.length > 1 ? page : page - 1;
            if (page === pageNumber) {
              fetchPaymentList(page, pageSize);
            } else {
              setPage(pageNumber);
            }
            toast.success(response.data.message, { ...(toastStyles as any) });
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, { ...(toastStyles as any) });
        });
    } catch (error) {
      toast.error(t.common.somethingWentWrong, { ...(toastStyles as any) });
    }
  };

  const handleExportReport = async () => {
    const values = getValues();
    const fromDate = values.fromDate || filter.fromDate;
    const toDate = values.toDate || filter.toDate;

    if (!fromDate || !toDate) {
      toast.error((t.payments as any).generateReportDateRequired, { ...(toastStyles as any) });
      return;
    }

    try {
      await GET(`/v1/payments/parties/${id}/reports`, { fromDate, toDate })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, { ...(toastStyles as any) });
            const downloadLink = response.data.data.download_link;
            try {
              const newWindow = window.open(downloadLink, "_blank");
              if (!newWindow || newWindow.closed || typeof newWindow.closed == "undefined") {
                throw new Error("Pop-up blocked");
              }
            } catch (e) {
              window.location.href = downloadLink;
            }
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          const msg = error?.response?.data?.message || (t.payments as any).generateReportError;
          toast.error(msg, { ...(toastStyles as any) });
        });
    } catch (error) {
      toast.error((t.payments as any).generateReportError, { ...(toastStyles as any) });
    }
  };

  const fetchPaymentList = useCallback(
    (pg: number, sz: number) => {
      dispatch(
        fetchPaymentsListData({
          id: id || "0",
          queryParams: {
            page: pg,
            size: sz,
            fromDate: filter.fromDate,
            toDate: filter.toDate,
            sortBy: filter.sortBy,
            sortOrder: filter.sortOrder,
          },
        })
      );
    },
    [dispatch, id, filter.fromDate, filter.toDate, filter.sortBy, filter.sortOrder]
  );

  const onApplyFilters = (data: PaymentsFilterType) => {
    dispatch(setPaymentsFilter({
      search: data.search || null,
      fromDate: data.fromDate || null,
      toDate: data.toDate || null,
      sortBy: data.sortBy,
      sortOrder: data.sortOrder,
    }));
    if (page === 1) {
      fetchPaymentList(1, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleClearFilters = () => {
    const defaults: PaymentsFilterType = {
      search: null,
      fromDate: null,
      toDate: null,
      sortBy: "id",
      sortOrder: "desc",
    };
    reset(defaults);
    dispatch(setPaymentsFilter(defaults));
    if (page === 1) {
      fetchPaymentList(1, pageSize);
    } else {
      setPage(1);
    }
  };

  useEffect(() => {
    fetchPaymentList(page, pageSize);
  }, [page, pageSize, filter.fromDate, filter.toDate, filter.sortBy, filter.sortOrder]);

  const totalPages = Math.ceil(totalPayments / pageSize);

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const start = totalPayments === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalPayments);

  return (
    <Layout>
      <div className="px-6 py-6">
        {/* Breadcrumb */}
        <nav className="text-sm font-semibold text-gray-500 mb-2">
          <Link to={route.dashboard} className="text-brand-dark hover:underline">
            {t.payments.breadcrumbHome}
          </Link>
          <span className="mx-2">&rsaquo;</span>
          <Link to={route.parties} className="text-brand-dark hover:underline">
            {t.payments.breadcrumbParties}
          </Link>
          <span className="mx-2">&rsaquo;</span>
          <span className="text-gray-500">{t.payments.breadcrumbPayments}</span>
        </nav>

        {/* Page title */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <h1 className="text-2xl font-bold text-brand-dark">
            {t.payments.pageTitle}
          </h1>
          {party?.firm_name && (
            <div className="text-right">
              <div className="text-lg font-semibold text-brand-dark">
                {party.firm_name}
              </div>
              {party.name && (
                <div className="text-sm text-gray-600 mt-0.5">
                  {party.name}
                  {party.phone && ` (+91 ${party.phone})`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <div className="flex flex-wrap gap-3 ml-auto">
            <Button variant="outline" className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm" onClick={() => setShowFilter(!showFilter)}>
              <Filter className="h-4 w-4 mr-2" />{t.payments.filterToggle}
            </Button>
            {canAccess("parties.payment") && (
              <>
                <Button className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm" onClick={() => handleShowCreateForm(2)}>
                  <Receipt className="h-4 w-4 mr-2" />{t.payments.actions.receivedPayment}
                </Button>
                <Button className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm" onClick={() => handleShowCreateForm(3)}>
                  <CreditCard className="h-4 w-4 mr-2" />{t.payments.actions.adjustment}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Summary cards: 4 plain white cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <Card className="p-4">
            <p className="text-sm text-gray-500">{t.payments.summary.totalInvoice}</p>
            <p className="mt-2 text-right text-xl md:text-2xl font-bold text-[#113F67]">
              {totalInvoice.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500">{t.payments.summary.totalReceived}</p>
            <p className="mt-2 text-right text-xl md:text-2xl font-bold text-[#113F67]">
              {totalPaymnet.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500">{t.payments.summary.totalAdjustment}</p>
            <p className="mt-2 text-right text-xl md:text-2xl font-bold text-[#113F67]">
              {totalNoteAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500">{t.payments.summary.balance}</p>
            <p className="mt-2 text-right text-xl md:text-2xl font-bold text-[#113F67]">
              {totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </Card>
        </div>

        {/* Filter panel */}
        {showFilter && (
          <Card className="mb-6">
            <CardHeader><CardTitle className="text-lg">{t.payments.filterTitle}</CardTitle></CardHeader>
            <CardContent className="pt-0 pb-4">
              <form onSubmit={handleSubmit(onApplyFilters)}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                  <div>
                    <label className="text-sm font-semibold text-[#113F67] mb-1 block">{t.payments.searchLabel}</label>
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A]" />
                      <Input className="pl-12 h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand" placeholder={t.payments.searchPlaceholder} {...register("search")} />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#113F67] mb-1 block">{(t.payments as any).fromDateLabel}</label>
                    <Input type="date" className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand" {...register("fromDate")} />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#113F67] mb-1 block">{(t.payments as any).toDateLabel}</label>
                    <Input type="date" className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand" {...register("toDate")} />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#113F67] mb-1 block">{t.payments.sortByLabel}</label>
                    <div className="relative">
                      <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                      <Controller name="sortBy" control={control} render={({ field }) => (
                        <ReactSelect styles={listSelectStyles} options={sortByOptions}
                          placeholder={t.payments.sortByPlaceholder}
                          value={sortByOptions.find((o) => o.value === field.value)}
                          onChange={(opt: any) => field.onChange(opt?.value || "id")} />
                      )} />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#113F67] mb-1 block">{t.payments.sortOrderLabel}</label>
                    <div className="relative">
                      <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                      <Controller name="sortOrder" control={control} render={({ field }) => (
                        <ReactSelect styles={listSelectStyles} options={sortOrderOptions}
                          placeholder={t.payments.sortOrderPlaceholder}
                          value={sortOrderOptions.find((o) => o.value === field.value)}
                          onChange={(opt: any) => field.onChange(opt?.value || "desc")} />
                      )} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                  <Button type="button" variant="outline" className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm" onClick={handleClearFilters}>
                    {t.payments.clearFilters}
                  </Button>
                  <Button type="button" className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm" onClick={handleExportReport}>
                    {t.payments.generateReport}
                  </Button>
                  <Button type="submit" className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm">
                    {t.payments.applyFilters}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Payment list */}
        <PaymentList
          payments={payments}
          pageNumber={page}
          pageSize={pageSize}
          isLoading={loading}
          handleDeletePayment={handleDeletePayment}
        />

        {/* Pagination */}
        {!loading && payments.length > 0 && (
          <div className="flex flex-wrap items-center justify-end md:justify-between gap-4 mt-4">
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
              <span>
                {start}-{end} of {totalPayments} {t.payments.pageOfLabel}
              </span>
              <span>{t.payments.pageSizeLabel}</span>
              <Select
                value={String(pageSize)}
                onValueChange={(val) => {
                  setPageSize(Number(val));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {getVisiblePages().map((p, i) =>
                typeof p === "string" ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                    ...
                  </span>
                ) : (
                  <Button
                    key={p}
                    variant={page === p ? "default" : "outline"}
                    className={`w-8 h-8 p-0 ${
                      page === p ? "bg-[#113F67] hover:bg-[#0d2f4d] text-white" : ""
                    }`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Button>
                )
              )}
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || totalPages === 0}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages || totalPages === 0}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Modals */}
        {isCreateModalOpen === 1 && editPayment !== null && (
          <EditForm handleEditPayment={handleSavePayment} />
        )}

        {isCreateModalOpen === 2 && (
          <PaymentForm handleSavePayment={handleSavePayment} />
        )}

        {isCreateModalOpen === 3 && (
          <NoteForm handleSavePayment={handleSavePayment} />
        )}
      </div>
    </Layout>
  );
};

export default PaymentsPage;
